/**
 * useSSEConnection Hook
 *
 * Manages SSE connection lifecycle: fetch, ReadableStream reading,
 * line buffering, and abort cleanup.
 */

import { useCallback } from 'react';

export type SSEConnectionOptions = {
  onLine: (line: string) => void;
  onDone: () => void;
  onError: (error: Error) => void;
};

export type SSEConnectionReturn = {
  connect: (url: string, body: object, signal: AbortSignal, options: SSEConnectionOptions) => Promise<void>;
};

export function useSSEConnection(): SSEConnectionReturn {
  const connect = useCallback(async (url: string, body: object, signal: AbortSignal, options: SSEConnectionOptions) => {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'pray-platform': 'Web',
      },
      credentials: 'include',
      body: JSON.stringify(body),
      signal,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    if (!response.body) {
      throw new Error('No response body');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    // eslint-disable-next-line no-constant-condition
    while (true) {
      // eslint-disable-next-line no-await-in-loop
      const { done, value } = await reader.read();

      if (done) {
        // Process remaining buffer before closing
        if (buffer.trim()) {
          options.onLine(buffer.trim());
        }
        options.onDone();
        break;
      }

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      // eslint-disable-next-line no-restricted-syntax
      for (const line of lines) {
        // eslint-disable-next-line no-continue
        if (!line.trim() || line.startsWith(':')) continue;
        options.onLine(line);
      }
    }
  }, []);

  return { connect };
}
